import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  LocalFireDepartment as FireIcon,
  Thermostat as ThermostatIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const HTCodeMaster = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    ht_code: '',
    ht_name: '',
    category: '',
    ht_process: '',
    austenitizing_temp_min: '',
    austenitizing_temp_max: '',
    quenching_media: '',
    tempering_temp_min: '',
    tempering_temp_max: '',
    hardness_min: '',
    hardness_max: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/masters/ht-codes');
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching HT code records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      ht_code: '',
      ht_name: '',
      category: '',
      ht_process: '',
      austenitizing_temp_min: '',
      austenitizing_temp_max: '',
      quenching_media: '',
      tempering_temp_min: '',
      tempering_temp_max: '',
      hardness_min: '',
      hardness_max: ''
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/masters/ht-codes/${currentRecord.ht_code}`, currentRecord);
      } else {
        await api.post('/masters/ht-codes', currentRecord);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving HT code record:', error);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['HT Code', 'Process Name', 'Category', 'Austenitizing Temp', 'Quenching Media', 'Tempering Temp', 'Hardness Range'],
      ...filteredRecords.map(record => [
        record.ht_code,
        record.ht_name || record.ht_process || '',
        record.category || '',
        `${record.austenitizing_temp_min || ''}-${record.austenitizing_temp_max || ''}°C`,
        record.quenching_media || '',
        `${record.tempering_temp_min || ''}-${record.tempering_temp_max || ''}°C`,
        `${record.hardness_min || ''}-${record.hardness_max || ''} HRC`
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ht_code_master.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getProcessColor = (code) => {
    const firstChar = code?.[0]?.toUpperCase();
    switch (firstChar) {
      case 'A': return 'success';    // Annealing
      case 'N': return 'info';       // Normalizing
      case 'H': return 'error';      // Hardening
      case 'T': return 'warning';    // Tempering
      case 'Q': return 'primary';    // Quenching
      default: return 'default';
    }
  };

  const getProcessName = (code) => {
    const processMap = {
      'A': 'Annealing',
      'N': 'Normalizing', 
      'H': 'Hardening',
      'T': 'Tempering',
      'Q': 'Quenching',
      'S': 'Stress Relief',
      'C': 'Carburizing',
      'I': 'Induction'
    };
    return processMap[code?.[0]?.toUpperCase()] || code;
  };

  const filteredRecords = records.filter(record =>
    record.ht_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.ht_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.ht_process?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FireIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            HT Code Master
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Heat treatment process codes and parameters management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total HT Codes
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Annealing
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.ht_code?.startsWith('A')).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Hardening
              </Typography>
              <Typography variant="h4" color="error.main">
                {records.filter(r => r.ht_code?.startsWith('H')).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Other Processes
              </Typography>
              <Typography variant="h4" color="info.main">
                {records.filter(r => !['A', 'H'].includes(r.ht_code?.[0])).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search HT codes..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>HT Code</strong></TableCell>
                <TableCell><strong>Process Name</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Temperature Range</strong></TableCell>
                <TableCell><strong>Quenching Media</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.map((record) => (
                <TableRow key={record.ht_code} hover>
                  <TableCell>
                    <Chip 
                      label={record.ht_code} 
                      color={getProcessColor(record.ht_code)} 
                      variant="outlined" 
                      size="small"
                      icon={<FireIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {record.ht_name || record.ht_process || getProcessName(record.ht_code)}
                    </Typography>
                  </TableCell>
                  <TableCell>{record.category || '-'}</TableCell>
                  <TableCell>
                    {record.austenitizing_temp_min && record.austenitizing_temp_max
                      ? `${record.austenitizing_temp_min}-${record.austenitizing_temp_max}°C`
                      : '-'
                    }
                  </TableCell>
                  <TableCell>{record.quenching_media || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleView(record)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(record)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit HT Code' : 'Add New HT Code'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HT Code"
                value={currentRecord.ht_code}
                onChange={(e) => setCurrentRecord({...currentRecord, ht_code: e.target.value})}
                disabled={editMode}
                helperText="e.g., A (Annealing), H (Hardening), N (Normalizing)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Process Name"
                value={currentRecord.ht_name}
                onChange={(e) => setCurrentRecord({...currentRecord, ht_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HT Process"
                value={currentRecord.ht_process}
                onChange={(e) => setCurrentRecord({...currentRecord, ht_process: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={currentRecord.category}
                  label="Category"
                  onChange={(e) => setCurrentRecord({...currentRecord, category: e.target.value})}
                >
                  <MenuItem value="Thermal">Thermal Treatment</MenuItem>
                  <MenuItem value="Chemical">Chemical Treatment</MenuItem>
                  <MenuItem value="Mechanical">Mechanical Treatment</MenuItem>
                  <MenuItem value="Surface">Surface Treatment</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Temperature Parameters */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                <ThermostatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Temperature Parameters
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Austenitizing Temp Min (°C)"
                type="number"
                value={currentRecord.austenitizing_temp_min}
                onChange={(e) => setCurrentRecord({...currentRecord, austenitizing_temp_min: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Austenitizing Temp Max (°C)"
                type="number"
                value={currentRecord.austenitizing_temp_max}
                onChange={(e) => setCurrentRecord({...currentRecord, austenitizing_temp_max: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tempering Temp Min (°C)"
                type="number"
                value={currentRecord.tempering_temp_min}
                onChange={(e) => setCurrentRecord({...currentRecord, tempering_temp_min: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tempering Temp Max (°C)"
                type="number"
                value={currentRecord.tempering_temp_max}
                onChange={(e) => setCurrentRecord({...currentRecord, tempering_temp_max: e.target.value})}
              />
            </Grid>

            {/* Quenching and Hardness */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Quenching Media</InputLabel>
                <Select
                  value={currentRecord.quenching_media}
                  label="Quenching Media"
                  onChange={(e) => setCurrentRecord({...currentRecord, quenching_media: e.target.value})}
                >
                  <MenuItem value="Oil">Oil</MenuItem>
                  <MenuItem value="Water">Water</MenuItem>
                  <MenuItem value="Air">Air</MenuItem>
                  <MenuItem value="Brine">Brine</MenuItem>
                  <MenuItem value="Polymer">Polymer</MenuItem>
                  <MenuItem value="Salt Bath">Salt Bath</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Hardness Min (HRC)"
                type="number"
                value={currentRecord.hardness_min}
                onChange={(e) => setCurrentRecord({...currentRecord, hardness_min: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expected Hardness Max (HRC)"
                type="number"
                value={currentRecord.hardness_max}
                onChange={(e) => setCurrentRecord({...currentRecord, hardness_max: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FireIcon />
            HT Process Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.ht_code} - {currentRecord.ht_name || currentRecord.ht_process}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Category</Typography>
                <Typography variant="body2">{currentRecord.category || '-'}</Typography>
              </Grid>
              
              {(currentRecord.austenitizing_temp_min || currentRecord.austenitizing_temp_max) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Austenitizing Temperature</Typography>
                  <Typography variant="body2">
                    {currentRecord.austenitizing_temp_min}°C - {currentRecord.austenitizing_temp_max}°C
                  </Typography>
                </Grid>
              )}
              
              {(currentRecord.tempering_temp_min || currentRecord.tempering_temp_max) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Tempering Temperature</Typography>
                  <Typography variant="body2">
                    {currentRecord.tempering_temp_min}°C - {currentRecord.tempering_temp_max}°C
                  </Typography>
                </Grid>
              )}
              
              {currentRecord.quenching_media && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Quenching Media</Typography>
                  <Typography variant="body2">{currentRecord.quenching_media}</Typography>
                </Grid>
              )}
              
              {(currentRecord.hardness_min || currentRecord.hardness_max) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Expected Hardness</Typography>
                  <Typography variant="body2">
                    {currentRecord.hardness_min} - {currentRecord.hardness_max} HRC
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HTCodeMaster;